import type { TemplateRef } from '@angular/core';
import { Injectable } from '@angular/core';
import { convertToParamMap } from '@angular/router';
import { ActivatedRoute } from '@freelancer/activated-route';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class Sidebar {
  private _isOpenSubject$ = new BehaviorSubject<boolean>(false);
  private _customTemplateSubject$ = new ReplaySubject<
    TemplateRef<any> | undefined
  >(1);
  private _heading$ = new ReplaySubject<TemplateRef<any> | undefined>(1);
  private _subheadingSubject$ = new ReplaySubject<TemplateRef<any> | undefined>(
    1,
  );
  private _activatedTab$ = new ReplaySubject<TemplateRef<any> | undefined>(1);
  private _deactivatedTab$ = new ReplaySubject<TemplateRef<any> | undefined>(1);

  constructor(private activatedRoute: ActivatedRoute) {}

  toggle(open?: boolean): void {
    if (isDefined(open)) {
      this._isOpenSubject$.next(open);
      return;
    }

    this._isOpenSubject$.next(!this._isOpenSubject$.getValue());
  }

  setCustomTemplate(templateRef?: TemplateRef<any>): void {
    this._customTemplateSubject$.next(templateRef);
  }

  customTemplateChanges(): Observable<TemplateRef<any> | undefined> {
    return this._customTemplateSubject$.asObservable();
  }

  setHeading(templateRef?: TemplateRef<any>): void {
    this._heading$.next(templateRef);
  }

  headingChanges(): Observable<TemplateRef<any> | undefined> {
    return this._heading$.asObservable();
  }

  setSubHeading(templateRef?: TemplateRef<any>): void {
    this._subheadingSubject$.next(templateRef);
  }

  subheadingChanges(): Observable<TemplateRef<any> | undefined> {
    return this._subheadingSubject$.asObservable();
  }

  setActiveTab(templateRef?: TemplateRef<any>): void {
    this._activatedTab$.next(templateRef);
  }

  setDeactivedTab(templateRef?: TemplateRef<any>): void {
    this._deactivatedTab$.next(templateRef);
  }

  activeTabChanges(): Observable<TemplateRef<any> | undefined> {
    return this._activatedTab$.asObservable();
  }

  deactivedTabChanges(): Observable<TemplateRef<any> | undefined> {
    return this._deactivatedTab$.asObservable();
  }

  isOpen(): Observable<boolean> {
    return this._isOpenSubject$.asObservable();
  }

  isAvailable(): Observable<boolean> {
    return this.activatedRoute.queryParams.pipe(
      map(queryParams => convertToParamMap(queryParams).has('show_sidebar')),
      distinctUntilChanged(),
    );
  }
}
